import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

const MOBILE_BREAKPOINT = 990;

class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onCustomMobileClick, true);

    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onCustomMobileClick, true);
  }

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    if (this.#closeCustomCategoryDetail()) {
      event.preventDefault();
      return;
    }

    this.#close(this.#getDetailsElement(event));
  };

  #onCustomMobileClick = (event) => {
    if (window.innerWidth > MOBILE_BREAKPOINT) return;

    const target = event.target instanceof Element ? event.target : null;

    if (!target || !this.contains(target)) return;

    /*
     * MOBILE MEGA MENU
     *
     * The complete category top row is clickable.
     *
     * If the category contains child links:
     * - icon click
     * - title/name click
     * - arrow click
     * - empty area of the row
     *
     * all open the inner category screen.
     *
     * If there are no child links, the original parent
     * category link continues to work normally.
     */

    const categoryTop = target.closest('.custom-mega-category__top');

    if (!categoryTop || !this.contains(categoryTop)) return;

    const category = categoryTop.closest('.custom-mega-category');

    if (!category) return;

    const links = [
      ...category.querySelectorAll(
        ':scope > .custom-mega-category__links > li > a'
      ),
    ];

    /*
     * No inner links:
     * keep the normal collection/category navigation.
     */
    if (!links.length) return;

    /*
     * Inner links exist:
     * prevent parent collection navigation and open
     * the mobile detail screen.
     */
    event.preventDefault();
    event.stopPropagation();

    this.#openCustomCategoryDetail(category);
  };

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) {
      return this.refs.details;
    }

    return event.target.closest('details') ?? this.refs.details;
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector(':scope > summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add(
          'menu-drawer--has-submenu-opened'
        );
      }

      const drawer = details.querySelector(
        ':scope > .menu-drawer, :scope > .menu-drawer__submenu'
      );

      onAnimationEnd(
        drawer || details,
        () => trapFocus(details),
        { subtree: false }
      );
    });
  }

  back(event) {
    if (
      window.innerWidth <= MOBILE_BREAKPOINT &&
      this.#closeCustomCategoryDetail()
    ) {
      event?.preventDefault?.();
      return;
    }

    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  #close(details) {
    const summary = details.querySelector(':scope > summary');

    if (!summary) return;

    this.#closeCustomCategoryDetail(true);

    summary.setAttribute('aria-expanded', 'false');

    details.classList.remove('menu-open');

    this.refs.menuDrawer.classList.remove(
      'menu-drawer--has-submenu-opened'
    );

    const drawer = details.querySelector(
      ':scope > .menu-drawer, :scope > .menu-drawer__submenu'
    );

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);

        if (details === this.refs.details) {
          removeTrapFocus();

          const openDetails = this.querySelectorAll(
            'details[open]:not(accordion-custom > details)'
          );

          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll(
      '.menu-drawer__animated-element'
    );

    allAnimated.forEach((element) => {
      element.addEventListener(
        'animationend',
        removeWillChangeOnAnimationEnd
      );
    });
  }

  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll(
      'accordion-custom .details-content'
    );

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add(
          'details-content--no-animation'
        );
      }
    });

    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove(
            'details-content--no-animation'
          );
        }
      });
    }, 100);
  }

  #openCustomCategoryDetail(category) {
    const root = category.closest('.custom-mobile-mega-root');

    if (!root) return;

    /*
     * Remove an already opened detail screen.
     */
    const existingDetail = root.querySelector(
      ':scope > .custom-mobile-category-detail'
    );

    if (existingDetail) {
      existingDetail.remove();
    }

    const heading = category.querySelector(
      ':scope > .custom-mega-category__top .custom-mega-category__heading'
    );

    const links = [
      ...category.querySelectorAll(
        ':scope > .custom-mega-category__links > li > a'
      ),
    ];

    if (!heading || !links.length) return;

    /*
     * Detail screen wrapper.
     */
    const detail = document.createElement('div');

    detail.className =
      'custom-mobile-category-detail is-active';

    detail.setAttribute(
      'data-custom-category-detail',
      'true'
    );

    /*
     * Back navigation.
     */
    const nav = document.createElement('div');

    nav.className =
      'custom-mobile-category-detail__nav';

    const back = document.createElement('button');

    back.type = 'button';

    back.className =
      'custom-mobile-category-detail__back';

    back.setAttribute(
      'aria-label',
      'Back to Shop'
    );

    back.innerHTML =
      '<span class="detail-back-text">Back to Shop</span>';

    back.addEventListener('click', () => {
      this.#closeCustomCategoryDetail();
    });

    nav.appendChild(back);

    /*
     * Category title.
     */
    const title = document.createElement('div');

    title.className =
      'custom-mobile-category-detail__title';

    title.innerHTML = heading.innerHTML;

    /*
     * Number of inner categories.
     */
    const count = document.createElement('div');

    count.className =
      'custom-mobile-category-detail__count';

    count.textContent = `${links.length} ${
      links.length === 1
        ? 'category'
        : 'categories'
    }`;

    /*
     * Inner category links.
     */
    const list = document.createElement('ul');

    list.className =
      'custom-mobile-category-detail__links';

    links.forEach((sourceLink) => {
      const li = document.createElement('li');

      const link = sourceLink.cloneNode(true);

      link.removeAttribute('aria-hidden');

      li.appendChild(link);

      list.appendChild(li);
    });

    /*
     * See All.
     */
    const seeAll = category.querySelector(
      ':scope > .custom-mega-category__see-all'
    );

    let seeAllClone = null;

    if (seeAll) {
      seeAllClone = seeAll.cloneNode(true);

      seeAllClone.classList.add(
        'custom-mobile-category-detail__see-all'
      );

      seeAllClone.style.display = '';
    }

    /*
     * Build detail screen.
     */
    detail.append(
      nav,
      title,
      count,
      list
    );

    if (seeAllClone) {
      detail.appendChild(seeAllClone);
    }

    root.appendChild(detail);

    root.classList.add(
      'custom-mobile-mega-root--detail-open'
    );

    this.refs.menuDrawer.classList.add(
      'menu-drawer--has-submenu-opened'
    );

    /*
     * Scroll mobile navigation to top.
     */
    const navigation = this.querySelector(
      '.menu-drawer__navigation'
    );

    if (navigation) {
      navigation.scrollTop = 0;
    }

    /*
     * Restore focus trap after detail screen opens.
     */
    requestAnimationFrame(() => {
      trapFocus(this.refs.details);
    });
  }

  #closeCustomCategoryDetail(force = false) {
    const root = this.querySelector(
      '.custom-mobile-mega-root--detail-open'
    );

    if (!root) return false;

    const detail = root.querySelector(
      ':scope > .custom-mobile-category-detail'
    );

    if (detail) {
      detail.remove();
    }

    root.classList.remove(
      'custom-mobile-mega-root--detail-open'
    );

    if (!force) {
      this.refs.menuDrawer.classList.remove(
        'menu-drawer--has-submenu-opened'
      );

      requestAnimationFrame(() => {
        trapFocus(this.refs.details);
      });
    }

    return true;
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define(
    'header-drawer',
    HeaderDrawer
  );
}

function reset(element) {
  element.classList.remove('menu-open');

  element.removeAttribute('open');

  element
    .querySelector(':scope > summary')
    ?.setAttribute(
      'aria-expanded',
      'false'
    );
}